
   import { defineComponent } from "vue";
   import { Signup, Trip } from "@/ApiInterfaces";
   import PublicHeader from "@/components/PublicHeader.vue";
   import StateSelect from "@/components/StateSelect.vue";
   import ModalLoading from "@/components/ModalLoading.vue";
   import ModalErrorMessage from "@/components/ModalErrorMessage.vue";
   import { groupBy, formatPhoneNumber, generateGuid } from "@/main";
   import { parseApiError, fieldError, getFieldErrorMsg, hasFieldError } from "@/apiErrorHandling";

   interface SignupData {
      trip: null | Trip;
      signups: Signup[];
      draggingSignup: null | Signup;
      draggingOverElement: null | HTMLElement;
      draggingOverCount: number;
      loading: boolean;
      submitted: boolean;
      errorMessage: null | string;
      fieldErrors: null | fieldError[];
      unsavedChanges: boolean;
   }

   export default defineComponent({
      name: "PublicSignups",
      data(): SignupData {
         return {
            trip: null,
            signups: [],
            draggingSignup: null,
            draggingOverElement: null,
            draggingOverCount: 0,
            loading: false,
            submitted: false,
            errorMessage: null,
            fieldErrors: null,
            unsavedChanges: false
         };
      },
      components: {
         PublicHeader,
         ModalLoading,
         ModalErrorMessage,
         StateSelect,
      },
      directives: {
         focus: {
            mounted(el) {
               el.focus();
            },
         },
      },
      watch: {
         signups: {
            handler(oldVal, newVal) {
               this.unsavedChanges = true;
            },
            deep: true
         }
      },
      created() {
         document.title = `Sign up for Tour # ${this.$route.params.tourId} | Group Trips`;

         window.addEventListener("beforeunload", (e) => {
            if (this.unsavedChanges && this.signups.length > 0) {
               e.preventDefault()
               const message = "You have not submitted your request to join this trip yet. Are you sure you wish to leave?"
               e.returnValue = message
               return message
            }
         })

         this.fetchTrip();
      },
      computed: {
         rooms(): { [key: string]: any[] } {
            return groupBy(this.signups, "roomNum");
         },
         roomCount(): number {
            return Object.keys(groupBy(this.signups, "roomNum")).length;
         },
         roomNumMax(): number {
            return this.signups
               .map((s) => Number(s.roomNum))
               .reduce((a: number, c: number) => (a > c ? a : c), 0);
         },
         tripDates(): string {
            if (this.trip) {
               return `${new Date(this.trip!.startDate.toString()).toLocaleDateString("en-US")} to ${new Date(this.trip!.returnDate.toString()).toLocaleDateString("en-US")}`;
            } else {
               return "";
            }
         }
      },
      methods: {
         fetchTrip(): void {
            try {
               fetch(
                  `/api/gttrips/${this.$route.params.gtUrl}/${this.$route.params.tourId}`
               )
                  .then((r) => {
                     if (r.ok) {
                        r.json()
                           .then((json) => {
                              this.trip = json as Trip;
                              document.title = `Sign up for Tour # ${this.trip.tourId} - ${this.trip.tourName} - ${this.tripDates} | Group Trips`;
                              return;
                           })
                           .catch((e) => {
                              console.error(e.toString());
                              window.location.href = '/' + this.$route.params.gtUrl;
                           });
                     } else {
                        // Trip not found so go to the group's home page
                        window.location.href = '/' + this.$route.params.gtUrl;
                     }
                  })
                  .catch((e) => {
                     console.error(e.toString());
                     window.location.href = '/' + this.$route.params.gtUrl;
                  });
            } catch (e) {
               console.error(e.toString());
               window.location.href = '/' + this.$route.params.gtUrl;
            }
         },
         submitSignups(): void {
            try {
               this.loading = true;

               fetch(
                  `/api/gtsignups/${this.$route.params.gtUrl}/${this.$route.params.tourId}`,
                  {
                     method: "post",
                     headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                     },
                     body: JSON.stringify(this.signups),
                  }
               )
                  .then((r) => {
                     this.loading = false;
                     if (r.ok) {
                        this.submitted = true;
                        this.unsavedChanges = false;
                     } else {
                        r.text().then((t) => {
                           const errs = parseApiError(t);
                           for (const err of errs.fieldErrors) {
                              if (err.index != null && err.index >= 0 && err.index < this.signups.length) {
                                 err.clientId = this.signups[err.index].clientId;
                              }
                           }
                           // Setting these will automatically update the disply and show an error
                           this.fieldErrors = errs.fieldErrors;
                           this.errorMessage = errs.message;
                        });
                     }
                  })
                  .catch((e) => {
                     this.loading = false;
                     this.errorMessage = "Something went wrong. Please try again later.";
                     console.error(e.toString());
                  });
            } catch (e) {
               this.loading = false;
               this.errorMessage = "Something went wrong. Please try again later.";
               console.error(e.toString());
            }
         },
         formatPhone(phoneRaw: string): string {
            if (phoneRaw) {
               let formatted = "";
               if (phoneRaw.length > 6)
                  formatted = `(${phoneRaw.substring(0, 3)}) ${phoneRaw.substring(
                     3,
                     6
                  )}-${phoneRaw.substring(6, 10)}`;
               else
                  formatted = `(${phoneRaw.substring(0, 3)}) ${phoneRaw.substring(
                     3,
                     6
                  )}`;

               return formatted;
            } else return "";
         },
         roomNumToChar(roomNum: number): string {
            return String.fromCharCode("A".charCodeAt(0) + roomNum - 1);
         },
         roomType(paxCount: number): string {
            switch (paxCount) {
               case 1:
                  return "Single";
               case 2:
                  return "Double";
               case 3:
                  return "Triple";
               default:
                  return "N/A";
            }
         },
         addPassenger(roomNum: number): void {
            this.signups.push({
               clientId: generateGuid(),
               firstName: "",
               lastName: "",
               phone: "",
               roomNum: roomNum,
               twoBeds: false,
               futureTripsNotify: false,
               isDeleted: false
            });
         },
         removePassenger(signup: Signup): void {
            let idx = this.signups.indexOf(signup);
            if (idx !== -1) {
               let name =
                  signup.firstName || signup.lastName
                     ? signup.firstName + " " + signup.lastName
                     : "New Passenger";
               if (confirm("Click OK to confirm removal of passenger " + name))
                  this.signups.splice(idx, 1);
            }
         },
         roomPaxCount(roomNum: number): number {
            return this.signups.filter((s) => s.roomNum == roomNum).length;
         },
         startDrag(event: DragEvent, item: Signup) {
            if (event && event.dataTransfer) {
               event.dataTransfer.dropEffect = "move";
               event.dataTransfer.effectAllowed = "move";
               this.draggingSignup = item;
            }
         },
         dragOver(event: DragEvent, roomNum: number): void {
            if (event && event.dataTransfer) {
               this.draggingOverCount++;

               if (this.draggingSignup) {
                  if (
                     this.draggingSignup.roomNum != roomNum &&
                     this.roomPaxCount(roomNum) < 3
                  ) {
                     if (event.target && event.target) {
                        let dragTarget = event.target as HTMLElement;
                        if (dragTarget.hasAttribute("dropzone")) {
                           dragTarget.style.backgroundColor = "lightgreen";
                           if (
                              this.draggingOverElement &&
                              this.draggingOverElement != dragTarget
                           ) {
                              this.draggingOverElement.style.backgroundColor = "";
                           }
                           this.draggingOverElement = dragTarget;
                        }
                     }
                     event.dataTransfer.dropEffect = "move";
                     event.dataTransfer.effectAllowed = "move";
                  }
                  event.preventDefault();
               }
            }
         },
         dragOut(event: DragEvent): void {
            if (event && event.target) {
               this.draggingOverCount--;
               if (this.draggingOverCount == 0) {
                  if (this.draggingOverElement)
                     this.draggingOverElement.style.backgroundColor = "";
               }
            }
         },
         dragDrop(roomNum: number): void {
            if (this.draggingSignup) {
               if (
                  this.draggingSignup.roomNum != roomNum &&
                  this.roomPaxCount(roomNum) < 3
               ) {
                  if (this.draggingOverElement)
                     this.draggingOverElement.style.backgroundColor = "";
                  this.draggingSignup.roomNum = roomNum;
               }
            }
            this.draggingOverCount = 0;
         },
         dragEnd(): void {
            if (this.draggingOverElement) {
               this.draggingOverElement.style.backgroundColor = "";
               this.draggingOverElement = null;
            }
            this.draggingOverCount = 0;
         },
      },
   });
